import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Seo } from '../components/Seo';
import { Car, Building2, Home as HomeIcon, Bike, FileText, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getServices } from '../utils/api';

const Home = () => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const data = await getServices();
        setServices(data);
      } catch (error) {
        // Log error for debugging - TODO: Replace with proper error tracking service in production
        console.error('Error fetching services:', error);
      }
    };
    fetchServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty - fetch once on component mount only
  const iconMap = {
    Car,
    Building2,
    Home: HomeIcon,
    Bike,
    FileText
  };

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Kerry M Holloway Insurance Agency - Prineville, Oregon | Commercial & Contractor Insurance"
        description="Independent insurance agency in Prineville, Oregon serving Central Oregon. Contractor insurance, CCB bonds, commercial, business, home, auto, and life insurance for Prineville, Bend, Redmond, and Madras."
        path="/"
      />
      {/* Hero Section - Sized for public/share view */}
      <section 
        className="relative flex items-center justify-center"
        style={{
          paddingTop: "340px",
          paddingBottom: "80px",
          minHeight: "650px",
          backgroundImage: "url('/hero-mountains.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center"
        }}
      >
        {/* Lighter overlay - less dominant */}
        <div className="absolute inset-0 bg-black/35"></div>
        
        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <div className="max-w-4xl mx-auto space-y-5">
            <h1 style={{ fontSize: "64px", lineHeight: "1.2" }} className="font-bold text-white drop-shadow-2xl">
              Insurance Without the Runaround
            </h1>
            
            <p style={{ fontSize: "28px" }} className="text-white leading-relaxed max-w-3xl mx-auto drop-shadow-lg">
              Clear, honest guidance—so you can make confident decisions.
            </p>
            
            <div className="pt-4">
              <Link to="/quote">
                <Button size="lg" className="bg-[#D94B5A] hover:bg-[#C43D4D] text-white font-medium px-10 py-7 text-lg transition-all duration-300 shadow-2xl">
                  Get a Quote
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-32 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#1F1F1F] mb-6">
              How I Help
            </h2>
            <p className="text-lg text-gray-600">
              Comprehensive insurance coverage for your vehicles, home, business, and more.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 max-w-5xl mx-auto mb-12">
            {services.slice(0, 4).map((service) => {
              const iconMap = {
                Car,
                Building2,
                Home: HomeIcon,
                Bike,
                FileText
              };
              const Icon = iconMap[service.icon];
              return (
                <div key={service.id} className="text-center group">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-[#F7F7F7] rounded-xl mb-4 group-hover:bg-[#D94B5A] transition-all duration-300">
                    {Icon && <Icon className="h-10 w-10 text-[#1F1F1F] group-hover:text-white transition-colors duration-300" />}
                  </div>
                  
                  <h3 className="text-xl font-bold text-[#1F1F1F] mb-2">{service.title}</h3>
                  
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {service.description.split('.')[0]}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="text-center">
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-2 border-[#1F1F1F] text-[#1F1F1F] hover:bg-[#1F1F1F] hover:text-white transition-all duration-300 px-8 py-6 text-base font-medium">
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* What You Can Expect Section */}
      <section className="py-32 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center space-y-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#1F1F1F]">
              What You Can Expect
            </h2>
            
            <div className="text-lg text-gray-700 leading-relaxed space-y-6">
              <p className="text-xl">
                No runaround. No confusion. No pressure.
              </p>
              
              <p>
                Just clear, honest guidance so you can make informed decisions about your coverage—without feeling like you're being sold something you don't need.
              </p>
              
              <p>
                When questions come up or situations change, you'll have someone you can call who knows your account and can help you work through it.
              </p>
            </div>
            
            <div className="pt-8">
              <Link to="/about">
                <Button size="lg" variant="outline" className="border-2 border-gray-900 text-[#1F1F1F] hover:bg-gray-900 hover:text-white transition-all duration-300 px-8 py-6 text-base font-medium">
                  Learn More About Our Approach
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="py-32 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center space-y-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#1F1F1F]">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-gray-700">
              Let's have a conversation about your insurance needs.
            </p>
            
            {/* Office Hours */}
            <div className="text-gray-600 text-lg">
              <p className="font-medium text-[#1F1F1F] mb-2">Office Hours</p>
              <p>Monday - Thursday: 8:00 AM - 5:00 PM</p>
              <p>Friday: 8:00 AM - 3:00 PM</p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center pt-6">
              <Link to="/quote">
                <Button size="lg" className="bg-[#D94B5A] hover:bg-red-800 text-white font-medium px-10 py-7 text-lg">
                  Get a Quote
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-2 border-gray-900 text-[#1F1F1F] hover:bg-gray-900 hover:text-white transition-all duration-300 px-10 py-7 text-lg font-medium">
                  Contact Us
                </Button>
              </Link>
            </div>

            <p className="text-base text-gray-600 pt-4">
              Already a client?{' '}
              <a
                href="https://g.page/r/CTz06hog7XU7EAE/review"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[#D94B5A] font-medium hover:underline"
                data-testid="home-google-review-link"
              >
                <Star className="h-4 w-4 fill-current" />
                Leave us a review on Google
              </a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
